import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, Building2, Globe } from "lucide-react";
import { Spinner } from "../components/common";
import { EmpCode } from "../components/StaffAvatar";
import { Button } from "../components/ui/button";
import { api, errMsg, fmtINR, fmtDate } from "../lib/api";
import { ReceiptHiddenCard } from "../components/ReceiptVisibilityNotice";   // Phase 169
import { PinQr } from "../components/PinQr";   // Phase 208
import { WatermarkOverlay } from "../components/watermark/WatermarkOverlay";   // Phase 224
import { MunicipalityLogo } from "../components/watermark/MunicipalityLogo";   // Phase 224b

/**
 * The printable A4-style receipt sheet. Shared by the staff receipt page (`/:code/receipt/:no`) and the
 * public Citizen Portal receipt (`/:code/pay/receipt/:token`, Phase 118) so both print identically.
 * `d` is `receipts.receipt_data`. `code` (Phase 208) enables the PIN QR → public property lookup / pay online.
 */
export function ReceiptSheet({ d, code }) {
  const isPortal = d.collection_channel === "citizen_portal" || d.source === "citizen_portal";
  return (
    <div id="receipt-sheet" className="relative bg-white border rounded-xl shadow-sm p-6 sm:p-8 text-black" data-testid="receipt-sheet">
      {/* Phase 224: the municipality's logo watermark, drawn behind the receipt (same geometry as the PDF) */}
      {code && <WatermarkOverlay code={code} />}
      <div className="relative">
      <div className="flex items-start justify-between border-b-2 border-black pb-4">
        <div className="flex items-center gap-3">
          {/* Phase 224b: the municipality's uploaded logo (falls back to the building badge until one exists) */}
          {code ? <MunicipalityLogo code={code} /> : (
            <div className="h-12 w-12 rounded-full border-2 border-black flex items-center justify-center">
              <Building2 className="h-6 w-6" />
            </div>
          )}
          <div>
            <p className="font-bold text-lg leading-tight">{d.municipality}</p>
            <p className="text-xs text-gray-600">{d.municipality_address || ""}</p>
            <p className="text-xs font-semibold mt-0.5">OFFICIAL TAX PAYMENT RECEIPT</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-mono font-bold" data-testid="receipt-number">{d.receipt_number}</p>
          <p className="text-xs text-gray-600">{fmtDate(d.receipt_date)} {d.payment_time || ""}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 py-4 text-sm border-b">
        <div><p className="text-xs text-gray-500">Property PIN</p><p className="font-mono font-semibold">{d.pin}</p></div>
        <div><p className="text-xs text-gray-500">Payment Mode</p><p className="font-semibold">{d.payment_mode}</p></div>
        <div><p className="text-xs text-gray-500">Owner</p><p className="font-semibold">{d.owner_name}</p></div>
        <div><p className="text-xs text-gray-500">Paid By</p><p className="font-semibold">{d.payer_name || d.owner_name}</p></div>
        <div className="col-span-2"><p className="text-xs text-gray-500">Address</p><p>{d.address || "-"}</p></div>
        {(d.zone_label || d.ward_label) && (
          <div><p className="text-xs text-gray-500">Zone / Ward</p><p>{[d.zone_label, d.ward_label].filter(Boolean).join(" / ")}</p></div>
        )}
        {d.transaction_ref && <div><p className="text-xs text-gray-500">Transaction Ref</p><p className="font-mono">{d.transaction_ref}</p></div>}
        {d.cheque_number && <div><p className="text-xs text-gray-500">Cheque No</p><p className="font-mono">{d.cheque_number} {d.bank_name ? `(${d.bank_name})` : ""}</p></div>}
        {d.dd_number && <div><p className="text-xs text-gray-500">DD No</p><p className="font-mono">{d.dd_number} {d.bank_name ? `(${d.bank_name})` : ""}</p></div>}
      </div>

      <table className="w-full text-sm my-4">
        <thead>
          <tr className="border-b-2 border-black text-left">
            <th className="py-2">#</th>
            <th className="py-2">Tax Head</th>
            <th className="py-2 text-right">Penalty</th>
            <th className="py-2 text-right">Discount</th>
            <th className="py-2 text-right">Amount Paid</th>
            <th className="py-2 text-right">Balance After</th>
          </tr>
        </thead>
        <tbody>
          {(d.line_items || []).map((li, i) => (
            <tr key={i} className="border-b">
              <td className="py-2">{i + 1}</td>
              <td className="py-2">{li.tax_head}{li.fy_label ? <span className="text-gray-500"> · {li.fy_label}</span> : ""}</td>
              <td className="py-2 text-right font-mono">{fmtINR(li.penalty || 0)}</td>
              <td className="py-2 text-right font-mono">{fmtINR(li.discount || 0)}</td>
              <td className="py-2 text-right font-mono">{fmtINR(li.amount)}</td>
              <td className="py-2 text-right font-mono">{fmtINR(li.balance_after)}</td>
            </tr>
          ))}
          {d.advance_amount > 0 && (
            <tr className="border-b" data-testid="receipt-advance-row">
              <td className="py-2">{(d.line_items || []).length + 1}</td>
              <td className="py-2">{d.advance_label ? <>{d.advance_label.split(" — ")[0]} <span className="text-gray-500">({d.advance_label.split(" — ").slice(1).join(" — ") || "excess payment"})</span></> : <>Advance credit <span className="text-gray-500">(no outstanding demand at the time)</span></>}</td>
              <td className="py-2 text-right font-mono">{fmtINR(0)}</td>
              <td className="py-2 text-right font-mono">{fmtINR(0)}</td>
              <td className="py-2 text-right font-mono">{fmtINR(d.advance_amount)}</td>
              <td className="py-2 text-right font-mono">—</td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr className="border-t">
            <td colSpan={4} className="py-1.5 text-right text-gray-600">Penalty applied</td>
            <td className="py-1.5 text-right font-mono" data-testid="receipt-penalty-amount">{fmtINR(d.penalty_amount || 0)}</td>
            <td />
          </tr>
          <tr>
            <td colSpan={4} className="py-1.5 text-right text-gray-600">Discount applied</td>
            <td className="py-1.5 text-right font-mono" data-testid="receipt-discount-amount">{fmtINR(d.discount_amount || 0)}</td>
            <td />
          </tr>
          <tr className="border-t-2 border-black">
            <td colSpan={4} className="py-2 font-bold">TOTAL PAID</td>
            <td className="py-2 text-right font-bold font-mono text-lg" data-testid="receipt-total-amount">{fmtINR(d.total_amount)}</td>
            <td />
          </tr>
        </tfoot>
      </table>

      <div className="flex items-end justify-between gap-4 pt-6 text-xs text-gray-600">
        {code && d.pin && <PinQr code={code} pin={d.pin} size={80} className="shrink-0" testId="receipt-pin-qr" />}
        <div className="flex-1 min-w-0">
          <p>Collected by: <span className="font-semibold text-black" data-testid="receipt-collected-by">
            {d.legacy_import
              ? <>{d.collected_by_name || "Imported (Legacy)"}{d.legacy_receipt_number && <span className="font-normal text-gray-600"> · original receipt no. {d.legacy_receipt_number}</span>}</>
              : d.collected_by_name
                ? <>{d.collected_by_name}<EmpCode code={d.collected_by_employee_id} className="text-gray-600 font-normal" /></>
                : d.collected_by}
          </span></p>
          {isPortal && (
            <p className="mt-1 max-w-md" data-testid="receipt-portal-line">
              <Globe className="h-3 w-3 inline mr-1 -mt-0.5" /> Paid online through the Citizen Portal
              {d.credited_to_label ? <> · credited to <span className="font-semibold text-black">{d.credited_to_label}</span></> : " · no assigned collector"}
              {d.gateway_mode === "simulated" && <span className="ml-1 text-amber-700">(test gateway — no money charged)</span>}
            </p>
          )}
          <p className="mt-4">This is a system-generated receipt of {d.municipality}.</p>
          <p>Subject to realization of cheque/DD where applicable.</p>
        </div>
        <div className="text-center shrink-0">
          <div className="h-14 w-32 border-b border-gray-400" />
          <p className="mt-1">Authorized Signatory</p>
        </div>
      </div>
      </div>
    </div>
  );
}

export default function ReceiptPage() {
  const { code, receiptNumber } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/t/${code}/receipts/${receiptNumber}`).then((r) => setReceipt(r.data)).catch((e) => setError(e));
  }, [code, receiptNumber]);

  // Phase 169: the municipality may have switched receipt viewing off for this role (payment stays recorded)
  if (error && error?.response?.status === 403) {
    return (
      <div className="min-h-screen bg-muted/50 py-6 px-4">
        <div className="max-w-2xl mx-auto space-y-4">
          <div className="flex items-center justify-between no-print">
            <Button variant="outline" onClick={() => navigate(-1)} data-testid="receipt-back-button"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
          </div>
          <ReceiptHiddenCard receiptNumber={receiptNumber} message={errMsg(error)} testId="receipt-hidden" />
        </div>
      </div>
    );
  }
  if (error) return <div className="p-8 text-center text-muted-foreground" data-testid="receipt-not-found">Receipt not found</div>;
  if (!receipt) return <Spinner />;

  return (
    <div className="min-h-screen bg-muted/50 py-6 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4 no-print">
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="receipt-back-button">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <Button onClick={() => window.print()} data-testid="receipt-print-button">
            <Printer className="h-4 w-4 mr-2" /> Print receipt
          </Button>
        </div>
        <ReceiptSheet d={receipt.receipt_data} code={code} />
      </div>
    </div>
  );
}
